import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './configure-inventory-warehouse-modal.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import Global from '../../../../../store/global-selectors';
import { inventoryWarehousePropertyNameOnShipFromObject } from '../../constants';
import messages from '../../messages';

/**
 * @param {object} props
 * @param {string} [props.inventoryWarehouseId]
 * @param {(newName: string) => void} props.onSave
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
const ConfigureInventoryWarehouseModal = ({ inventoryWarehouseId = '', onSave, onClose }) => {
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const selectedInventoryWarehouse = useSelector((state) => Global.selectSellerInventoryWarehouseById(state, { inventoryWarehouseId }));
  const [warehouseName, setWarehouseName] = useState(selectedInventoryWarehouse?.name);
  const linkedShipFroms = useSelector((state) =>
    Global.selectSellerShipFromsByPropertyValue(state, {
      property: inventoryWarehousePropertyNameOnShipFromObject,
      value: inventoryWarehouseId,
    }),
  );
  const onChange = (value) => setWarehouseName(value);
  const onSaveClicked = useCallback(() => {
    setIsLoading(true);
    onSave(warehouseName);
  }, [setIsLoading, onSave, warehouseName]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <FormattedMessage {...messages.configureInventoryWarehouseModalTitle} />
        </ModalHeader>
        <ModalBody className={style['configure-inventory-warehouse-body']}>
          {isLoading ? <LoadingIndicator className={style['loading-indicator']} /> : null}
          {!isLoading && (
            <>
              <span className={style.descriptor}>
                <FormattedMessage {...messages.configureInventoryWarehouseModalHelperText} />
              </span>
              <div>
                <div className={style['configure-inventory-warehouse-input-label']}>
                  <FormattedMessage {...messages.configureInventoryWarehouseModalInputLabel} />
                </div>
                <Input
                  className={style['configure-inventory-warehouse-input']}
                  id='configure-inventory-warehouse-inventory-warehouse-name'
                  name='configure-inventory-warehouse-inventory-warehouse-name'
                  onChange={onChange}
                  value={warehouseName}
                />
              </div>
              {inventoryWarehouseId && !!linkedShipFroms.length ? (
                <div className={style['linked-shipfroms']}>
                  <span className={style['linked-shipfroms-label']}>
                    <FormattedMessage {...messages.configureInventoryWarehouseModalLinkedShipFromsLabel} />
                  </span>
                  <ul className={style['linked-shipfroms-list']}>
                    {linkedShipFroms.map(({ name }) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </ModalBody>
        {!isLoading && (
          <ModalFooter className={style['configure-inventory-warehouse-footer']}>
            <ModalFooterCancelButton />
            <Button disabled={!warehouseName} onClick={onSaveClicked} theme='primary'>
              <FormattedMessage {...messages.configureInventoryWarehouseModalSaveButton} />
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </ModalPortal>
  );
};

export default ConfigureInventoryWarehouseModal;
